import { useEffect, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import _ from "lodash";
import { Select } from "@shopify/polaris";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { FIXED_LEAVE_TYPE_CODES } from "features/leave-calendar/leaveCalendar.constants";
import { API_URL } from "../../constants";

function FileTemplateSelect({
  value,
  onChange,
  isRequired,
  disabled = false,
  isCustomerApproved = false,
  viewAll = false,
  templateId = "",
  error: propsErr = "",
  subject,
  restrictions,
  label: labelProp,
}) {
  const { t } = useTranslation();
  const [collection, setCollection] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const { companyId } = useParams();
  const [error, setError] = useState(propsErr);

  const handleChange = (selectedValue) => {
    if (isRequired && !selectedValue) {
      setError(t("field_is_required"));
    } else {
      setError("");
    }

    onChange(selectedValue);
  };

  useEffect(() => {
    async function fetchData() {
      try {
        setIsLoading(true);
        const collection = await fetch(companyId);
        // Step 1: Apply restrictions first
        let filteredCollection = collection;
        if (restrictions && restrictions.length) {
          filteredCollection = collection.filter(
            (templateEntity) => !restrictions.includes(templateEntity.subject)
          );
        }
        let dataCollection = [];
        // get templates based on parent template Id
        if (templateId) {
          dataCollection = _.flatMap(
            _.filter(filteredCollection, (row) => {
              return row.id === templateId; // Filter by templateId
            }),
            (row) =>
              _.map(row.relatedTo, (related) => ({
                label: related.name,
                value: related.id,
              }))
          );
        }
        // filter template based on subject
        else if (subject) {
          const subjects =
            subject === "user"
              ? [subject, ...FIXED_LEAVE_TYPE_CODES]
              : [subject];

          dataCollection = _.map(
            _.filter(filteredCollection, (row) => {
              return subjects.includes(row.subject);
            }),
            (row) => ({
              label: row.name,
              value: row.id,
            })
          );
        } else {
          dataCollection = _.map(
            _.filter(filteredCollection, (row) => {
              return (
                !(
                  row.isValidFinancialApprovalRequired === true &&
                  isCustomerApproved === false
                ) || viewAll
              );
            }),
            (row) => ({
              label: row.name,
              value: row.id,
            })
          );
        }
        dataCollection.unshift({ label: " - ", value: "" });
        setCollection(dataCollection);
        setIsLoading(false);
      } catch (e) {
        setIsLoading(false);
      }
    }
    fetchData();
  }, [companyId, templateId, restrictions]);

  return (
    <div className="file-template-select">
      <Select
        label={labelProp || t("file_template_select")}
        options={collection}
        onChange={handleChange}
        value={value}
        disabled={isLoading || disabled}
        error={propsErr}
      />
    </div>
  );
}

export default FileTemplateSelect;

FileTemplateSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  value: PropTypes.string,
  subject: PropTypes.string,
  isRequired: PropTypes.bool,
  restrictions: PropTypes.arrayOf(PropTypes.string),
  label: PropTypes.string,
};

FileTemplateSelect.defaultProps = {
  isRequired: false,
  label: undefined,
};

function fetch(companyId) {
  const url = `${API_URL}/${companyId}/files/templates/fetch-file-templates`;
  return axios.get(url).then((response) => response.data);
}
